/**
 * React Query hooks for common API operations
 */

import {
  useQuery,
  useMutation,
  useInfiniteQuery,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
  type UseInfiniteQueryOptions,
} from '@tanstack/react-query'
import { useCallback } from 'react'
import type { ApiClient } from '../client/api-client'
import type { RequestConfig, ApiResponse, PaginatedResponse } from '../types'
import { queryKeys, queryOptions, mutationOptions } from './query-client'

/**
 * Hook to get the API client instance
 * This should be provided via context in a real application
 */
let globalApiClient: ApiClient | null = null

export function setGlobalApiClient(client: ApiClient) {
  globalApiClient = client
}

function useApiClient(): ApiClient {
  if (!globalApiClient) {
    throw new Error('API client not configured. Call setGlobalApiClient() first.')
  }
  return globalApiClient
}

/**
 * Generic fetch hook
 */
export function useApiFetch<TData = any>(
  key: readonly unknown[],
  url: string,
  config?: RequestConfig,
  options?: Omit<UseQueryOptions<TData>, 'queryKey' | 'queryFn'>
) {
  const apiClient = useApiClient()

  return useQuery({
    ...queryOptions.standard(key, () =>
      apiClient.get<TData>(url, config).then(response => response.data)
    ),
    ...options,
  })
}

/**
 * Generic mutation hook
 */
export function useApiMutation<TData = any, TVariables = any>(
  mutationFn: (variables: TVariables) => Promise<ApiResponse<TData>>,
  options?: UseMutationOptions<ApiResponse<TData>, Error, TVariables>
) {
  return useMutation({
    ...mutationOptions.standard(mutationFn),
    ...options,
  })
}

/**
 * GET request hook
 */
export function useGet<TData = any>(
  key: readonly unknown[],
  url: string,
  config?: RequestConfig,
  options?: Omit<UseQueryOptions<TData>, 'queryKey' | 'queryFn'>
) {
  return useApiFetch<TData>(key, url, config, options)
}

/**
 * POST request hook
 */
export function usePost<TData = any, TVariables = any>(
  url: string,
  options?: UseMutationOptions<ApiResponse<TData>, Error, TVariables>
) {
  const apiClient = useApiClient()

  return useApiMutation<TData, TVariables>(
    variables => apiClient.post<TData>(url, variables),
    options
  )
}

/**
 * PUT request hook
 */
export function usePut<TData = any, TVariables = any>(
  url: string,
  options?: UseMutationOptions<ApiResponse<TData>, Error, TVariables>
) {
  const apiClient = useApiClient()

  return useApiMutation<TData, TVariables>(
    variables => apiClient.put<TData>(url, variables),
    options
  )
}

/**
 * PATCH request hook
 */
export function usePatch<TData = any, TVariables = any>(
  url: string,
  options?: UseMutationOptions<ApiResponse<TData>, Error, TVariables>
) {
  const apiClient = useApiClient()

  return useApiMutation<TData, TVariables>(
    variables => apiClient.patch<TData>(url, variables),
    options
  )
}

/**
 * DELETE request hook
 */
export function useDelete<TData = any>(
  url: string,
  options?: UseMutationOptions<ApiResponse<TData>, Error, void>
) {
  const apiClient = useApiClient()

  return useApiMutation<TData, void>(() => apiClient.delete<TData>(url), options)
}

/**
 * Paginated query hook
 */
export function usePaginatedQuery<TData = any>(
  key: readonly unknown[],
  url: string,
  page = 1,
  limit = 10,
  config?: RequestConfig,
  options?: Omit<UseQueryOptions<PaginatedResponse<TData>>, 'queryKey' | 'queryFn'>
) {
  const apiClient = useApiClient()

  return useQuery({
    ...queryOptions.standard([...key, 'paginated', { page, limit }], () =>
      apiClient
        .get<PaginatedResponse<TData>>(url, {
          ...config,
          params: { ...config?.params, page, limit },
        })
        .then(response => response.data)
    ),
    ...options,
  })
}

/**
 * Infinite query hook for pagination
 */
export function useInfiniteApiQuery<TData = any>(
  key: readonly unknown[],
  url: string,
  config?: RequestConfig,
  options?: Omit<
    UseInfiniteQueryOptions<PaginatedResponse<TData>>,
    'queryKey' | 'queryFn' | 'getNextPageParam'
  >
) {
  const apiClient = useApiClient()

  return useInfiniteQuery({
    queryKey: [...key, 'infinite'],
    queryFn: ({ pageParam = 1 }) =>
      apiClient
        .get<PaginatedResponse<TData>>(url, {
          ...config,
          params: { ...config?.params, page: pageParam },
        })
        .then(response => response.data),
    getNextPageParam: lastPage => {
      if (lastPage.pagination.hasNext) {
        return lastPage.pagination.page + 1
      }
      return undefined
    },
    initialPageParam: 1,
    ...options,
  })
}

/**
 * Search query hook with debouncing
 */
export function useSearchQuery<TData = any>(
  query: string,
  url: string,
  config?: RequestConfig,
  options?: Omit<UseQueryOptions<TData[]>, 'queryKey' | 'queryFn'>
) {
  const apiClient = useApiClient()

  return useQuery({
    queryKey: queryKeys.search(query),
    queryFn: () =>
      apiClient
        .get<TData[]>(url, {
          ...config,
          params: { ...config?.params, q: query },
        })
        .then(response => response.data),
    enabled: query.length > 0,
    staleTime: 30 * 1000, // 30 seconds
    ...options,
  })
}

/**
 * Cache management hooks
 */
export function useCacheUtils() {
  const queryClient = useQueryClient()

  const invalidateQueries = useCallback(
    (key: readonly unknown[]) => {
      return queryClient.invalidateQueries({ queryKey: key })
    },
    [queryClient]
  )

  const refetchQueries = useCallback(
    (key: readonly unknown[]) => {
      return queryClient.refetchQueries({ queryKey: key })
    },
    [queryClient]
  )

  const removeQueries = useCallback(
    (key: readonly unknown[]) => {
      return queryClient.removeQueries({ queryKey: key })
    },
    [queryClient]
  )

  const setQueryData = useCallback(
    <TData>(key: readonly unknown[], data: TData) => {
      return queryClient.setQueryData(key, data)
    },
    [queryClient]
  )

  const getQueryData = useCallback(
    <TData>(key: readonly unknown[]) => {
      return queryClient.getQueryData<TData>(key)
    },
    [queryClient]
  )

  const prefetchQuery = useCallback(
    <TData>(key: readonly unknown[], fetcher: () => Promise<TData>, staleTime?: number) => {
      return queryClient.prefetchQuery({
        queryKey: key,
        queryFn: fetcher,
        ...(staleTime !== undefined && { staleTime }),
      })
    },
    [queryClient]
  )

  return {
    invalidateQueries,
    refetchQueries,
    removeQueries,
    setQueryData,
    getQueryData,
    prefetchQuery,
  }
}

/**
 * Optimistic update hook
 */
export function useOptimisticMutation<TData, TVariables>(
  mutationFn: (variables: TVariables) => Promise<ApiResponse<TData>>,
  queryKey: readonly unknown[],
  optimisticUpdate: (oldData: TData | undefined, variables: TVariables) => TData,
  options?: UseMutationOptions<
    ApiResponse<TData>,
    Error,
    TVariables,
    { previousData: TData | undefined }
  >
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn,
    onMutate: async (variables): Promise<{ previousData: TData | undefined }> => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey })

      // Snapshot previous value
      const previousData = queryClient.getQueryData<TData>(queryKey)

      // Optimistically update
      queryClient.setQueryData<TData>(queryKey, old => optimisticUpdate(old, variables))

      // Return context with previous data
      return { previousData }
    },
    onError: (error, variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData)
      }
      options?.onError?.(error, variables, context)
    },
    onSettled: () => {
      // Refetch after mutation
      queryClient.invalidateQueries({ queryKey })
    },
    ...options,
  })
}
